<template>
  <div class="eula-outer-wrapper">
    <h1>{{ title }}</h1>
    <div ref="content" class="eula-inner-wrapper" @scroll="handleEulaScroll">
      <Editor
        ref="editor"
        :model-value="eula.license"
        :readonly="true"
        @load="editorLoad"
      >
      </Editor>
    </div>
    <div class="fadeaway"></div>
    <div class="eula-footer">
      <div>
        <span class="helper">
          Must read entire agreement before accepting
          <i class="pi pi-check" :class="{ checked: scrolledEulaMessage }" />
        </span>

        <Button
          class="p-button p-button-info"
          :disabled="disable || !scrolledEulaMessage"
          @click="$emit('accept', eula.id)"
        >
          Accept
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import type { Eula } from "gql/api";
import Editor from "primevue/editor";

defineEmits<{ (e: "accept", acceptedId: string): void }>();
const props = defineProps<{
  disable?: boolean;
  title: string;
  eula: Eula;
}>();

const scrolledEulaMessage = ref(false);
const content = ref<HTMLDivElement | null>(null);
const editor = ref();

const toggleCanAcceptIfScrolled = (el: HTMLDivElement) => {
  const reachedBottom =
    Math.ceil(el.offsetHeight + el.scrollTop) >= el.scrollHeight;
  if (reachedBottom) {
    scrolledEulaMessage.value = true;
  }
};

watch([content], () => {
  if (editor.value) {
    toggleCanAcceptIfScrolled(editor.value);
  }
});

const handleEulaScroll = (e: Event) => {
  const target: HTMLDivElement = e.target as HTMLDivElement;
  toggleCanAcceptIfScrolled(target);
  const reachedBottom =
    Math.ceil(target.offsetHeight + target.scrollTop) >= target.scrollHeight;
  if (reachedBottom) {
    scrolledEulaMessage.value = true;
  }
};

const editorLoad = () => {
  const delta = editor.value.quill.clipboard.convert({
    html: props.eula.license || ""
  });
  nextTick(() => editor.value.quill.setContents(delta));
};
</script>

<style lang="scss" scoped>
.eula-outer-wrapper {
  position: absolute;
  margin: 0 auto;
  display: block;
  top: 200px;
  left: 0;
  right: 0;
  bottom: 0;
  width: 800px;
  max-height: 800px;
  border: 1px solid var(--gray-400);

  h1 {
    font-size: 30px;
    text-align: center;
    display: inline-block;
    width: 100%;
    height: 100px;
    line-height: 100px;
    margin: 0;
  }

  .eula-inner-wrapper {
    width: 798px;
    height: 640px;
    overflow-y: scroll;
    padding-bottom: 20px;
  }

  .fadeaway {
    position: absolute;
    top: 640px;
    width: 785px;
    height: 100px;
    background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  .eula-footer {
    width: 100%;
    max-width: 800px;
    height: 40px;
    text-align: right;
    div {
      .helper {
        font-style: italic;
        display: inline-block;
      }
      i {
        font-style: initial;
        visibility: hidden;
        margin-left: 3px;
        font-weight: bold;
        color: var(--success-400);
        &.checked {
          visibility: visible;
        }
      }
      margin: 7px 10px 0 0;
      height: 40px;
      line-height: 40px;
    }
  }
}

:deep(.p-editor-container) {
  width: 100%;
  max-width: 800px;
  margin-bottom: 20px;
  border: none;
  margin: 0;
  padding: 0 0 30px 0;

  .p-editor-toolbar {
    display: none;
  }

  .p-editor-content {
    border: none;
    .ql-editor {
      background: none;
    }
  }
}

button {
  margin-left: 10px;
  align-self: flex-end;
}
[data-theme="dark"] {
  .fadeaway {
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0) 100%
    );
  }
}
</style>
